import { AnimatePresence, motion } from "framer-motion";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, SkipBack, SkipForward, X, Youtube } from "lucide-react";
import { useState } from "react";

export const PlayerBar = () => {
  const { current, open, setOpen, next, prev } = usePlayer();
  const [expanded, setExpanded] = useState(true);

  if (!current) return null;

  return (
    <>
      {/* Fullscreen video modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-void/95 backdrop-blur-md grid place-items-center p-4 md:p-8"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="min-w-0">
                  <p className="font-mono uppercase tracking-[0.2em] text-[11px] text-signal mb-1">
                    {current.cat} · {current.artist}
                  </p>
                  <h3 className="font-display uppercase tracking-tight text-white text-2xl md:text-4xl leading-none truncate">
                    {current.title}
                  </h3>
                </div>
                <button data-testid="close-video-modal" onClick={() => setOpen(false)} className="grid place-items-center h-11 w-11 border border-white/15 text-white hover:bg-signal hover:text-void hover:border-signal transition-colors shrink-0 ml-4">
                  <X size={20} />
                </button>
              </div>
              <div className="relative aspect-video w-full border border-white/10 bg-black overflow-hidden">
                <iframe
                  key={current.ytId}
                  data-testid="youtube-embed"
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${current.ytId}?autoplay=1&rel=0`}
                  title={current.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="flex items-center justify-between mt-4">
                <button data-testid="modal-prev" onClick={prev} className="flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-white/60 hover:text-white transition-colors">
                  <SkipBack size={16} /> Prev
                </button>
                <a href={`https://www.youtube.com/watch?v=${current.ytId}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-white/60 hover:text-signal transition-colors">
                  <Youtube size={16} /> Watch on YouTube
                </a>
                <button data-testid="modal-next" onClick={next} className="flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-white/60 hover:text-white transition-colors">
                  Next <SkipForward size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky now-playing bar */}
      {!open && expanded && (
        <motion.div
          data-testid="player-bar"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
          className="fixed bottom-4 left-4 right-4 md:left-8 md:right-8 z-50"
        >
          <div className="mx-auto max-w-3xl backdrop-blur-2xl bg-void/70 border border-white/10 flex items-center gap-3 md:gap-5 p-2.5 md:p-3">
            <img src={current.cover} alt={current.title} className="h-12 w-12 md:h-14 md:w-14 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-mono uppercase tracking-[0.18em] text-[9px] text-signal truncate">Now Playing · {current.artist}</p>
              <p className="font-display uppercase tracking-tight text-white text-lg md:text-xl leading-tight truncate">{current.title}</p>
            </div>
            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <button data-testid="bar-prev" onClick={prev} className="grid place-items-center h-9 w-9 text-white/60 hover:text-white transition-colors">
                <SkipBack size={16} />
              </button>
              <button data-testid="bar-play" onClick={() => setOpen(true)} className="grid place-items-center h-11 w-11 rounded-full bg-signal text-void hover:bg-white transition-colors">
                <Play size={18} className="fill-current ml-0.5" />
              </button>
              <button data-testid="bar-next" onClick={next} className="grid place-items-center h-9 w-9 text-white/60 hover:text-white transition-colors">
                <SkipForward size={16} />
              </button>
              <button data-testid="bar-close" onClick={() => setExpanded(false)} className="grid place-items-center h-9 w-9 text-white/40 hover:text-white transition-colors ml-1">
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};
